/**
 * Mutation Queue - Offline support for write operations.
 *
 * When the browser is offline, mutations (POST/PUT/DELETE) are queued
 * in memory and replayed when connectivity is restored.
 *
 * Queue is in-memory only (lost on page refresh). For production,
 * consider using IndexedDB for persistence.
 */

import { useAuthStore } from '../stores/authStore';

export interface QueuedMutation {
  url: string;
  method: string;
  body?: string;
  timestamp: number;
}

// In-memory queue
const mutationQueue: QueuedMutation[] = [];

const QUEUE_KEY = 'signa_offline_queue';

/**
 * Add a mutation to the offline queue and persist to sessionStorage.
 */
export function queueMutation(mutation: Omit<QueuedMutation, 'timestamp'>): void {
  const entry: QueuedMutation = { ...mutation, timestamp: Date.now() };
  mutationQueue.push(entry);
  try {
    sessionStorage.setItem(QUEUE_KEY, JSON.stringify(mutationQueue));
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * Replay all queued mutations and clear the queue.
 * Returns the number of successfully replayed mutations.
 */
export async function replayMutations(): Promise<number> {
  // Load from sessionStorage first (in case page was reloaded while offline)
  try {
    const stored = sessionStorage.getItem(QUEUE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as QueuedMutation[];
      if (parsed.length > 0) {
        mutationQueue.length = 0;
        mutationQueue.push(...parsed);
      }
    }
  } catch {
    // ignore parse errors
  }

  const token = useAuthStore.getState().token;
  let replayed = 0;
  const stillPending: QueuedMutation[] = [];

  for (const mutation of mutationQueue) {
    try {
      await fetch(mutation.url, {
        method: mutation.method,
        body: mutation.body,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      replayed++;
    } catch {
      // Keep failed mutations in the queue for retry
      stillPending.push(mutation);
    }
  }

  mutationQueue.length = 0;
  mutationQueue.push(...stillPending);
  try {
    if (stillPending.length > 0) {
      sessionStorage.setItem(QUEUE_KEY, JSON.stringify(stillPending));
    } else {
      sessionStorage.removeItem(QUEUE_KEY);
    }
  } catch {
    // ignore
  }

  return replayed;
}

/**
 * Get pending mutation count.
 */
export function getPendingMutationCount(): number {
  return mutationQueue.length;
}

/**
 * Clear the mutation queue.
 */
export function clearMutationQueue(): void {
  mutationQueue.length = 0;
  try {
    sessionStorage.removeItem(QUEUE_KEY);
  } catch {
    // ignore
  }
}
